import {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {AffiliatedEntityProfile} from '../../../../../../components/affiliated-entity-profile/affiliated-entity-profile';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useOffice} from '../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {CaregiverMode} from '../../../../../../enums/caregiver-mode';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {Box, Card, SxProps, Typography} from '@mui/material';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useObserveViewport} from '../../../../../../hooks';

export default function MyAffiliationsEntityMeOfficeScreen() {
  let {id}: {id: string} = useParams();

  const officeContext: OfficeContextType = useOffice();

  const {isRunningMobile} = useObserveViewport();

  const {t} = useTranslation();

  const {state: authState, hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Settings_Entity_Profile);

  // Redirect url
  let redirectUrl: string = ScreenPaths.Office;
  switch (officeContext.state.currentCaregiverModeEnabled) {
    case CaregiverMode.Informal:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${authState.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`;
      break;
    case CaregiverMode.Professional:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Activity}`;
      break;
    case CaregiverMode.SystemAdministrator:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Activity}`;
      break;
    case CaregiverMode.Partner:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_Partner}${ScreenPaths.Partner_Home}`;
      break;
    default:
      redirectUrl = ScreenPaths.Office;
      break;
  }

  const entityName = useMemo(
    () => officeContext.state.entityForm?.shortName || officeContext.state.entityForm?.name,
    [officeContext.state.entityForm?.shortName, officeContext.state.entityForm?.name]
  );

  const boxWrapper: SxProps = {display: 'flex', flexDirection: 'column', gap: '20px'};

  const cardSx: SxProps = {
    padding: {xs: '40px 10px 51px 10px', md: '40px 66px 51px 66px'},
    minHeight: `calc(100vh - 280px)`,
    borderRadius: '10px',
  };

  const headerSubtitleSx: SxProps = {
    fontSize: '18px',
    fontWeight: 400,
    color: 'white',
    maxWidth: {xs: '250px', sm: '100%'},
  };

  return (
    <Box sx={boxWrapper}>
      <GenericHeader
        titleCrumbs={
          entityName
            ? isRunningMobile
              ? [
                  {
                    label: t(
                      translations.office.professional_caregiver.profile.generic_header.title
                    ),
                    link: `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`,
                  },
                ]
              : [
                  {
                    label: t(
                      translations.office.professional_caregiver.profile.generic_header.title
                    ),
                    link: `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`,
                  },
                  {
                    label: t(translations.profile.affiliations_section_title),
                    link: '#',
                  },
                  {label: entityName, link: '#'},
                ]
            : undefined
        }
        isLoading={officeContext.state.entityFormLoadingState}
        leftColumn={
          <Typography sx={headerSubtitleSx} noWrap>
            {t(
              translations.office.professional_caregiver.settings.generic_header.subtitle
                .institution.write
            )}
          </Typography>
        }
      />
      <Card sx={cardSx} elevation={0}>
        <AffiliatedEntityProfile entityId={id} redirectUrl={redirectUrl} canWrite={canWrite} />
      </Card>
    </Box>
  );
}
